import {
  AvailableFeatureNames,
  FeatureToggleConfig,
  FeatureToggleOverrides,
} from './types';

export type MergedFeatureToggles = Record<
  AvailableFeatureNames,
  FeatureToggleConfig
>;

export const mergeFeatureFlagsConfigs = ({
  defaultConfig,
  overrides,
  urlOverrides,
}: {
  defaultConfig: FeatureToggleOverrides;
  overrides?: Partial<FeatureToggleOverrides>;
  urlOverrides?: Partial<FeatureToggleOverrides>;
}): MergedFeatureToggles => {
  return Object.fromEntries(
    Object.entries(defaultConfig).map(([featureName, featureConfig]) => {
      const override = overrides?.[featureName as AvailableFeatureNames];
      const urlOverride = urlOverrides?.[featureName as AvailableFeatureNames];
      const combinedFeatureFlag = extendCurrentDefaultConfigWithOverrides(
        featureConfig,
        urlOverride || override, // url overrides take precedence over remote overrides
      );

      return [featureName, combinedFeatureFlag];
    }),
  ) as MergedFeatureToggles;
};

const extendCurrentDefaultConfigWithOverrides = (
  currentDefaultConfig: FeatureToggleConfig,
  overrideConfig: FeatureToggleConfig | undefined,
) => {
  // if the override is a simple string value for the AB test winner variant, then we need to merge it with the default config
  // to ensure that the code that uses the `experiment.mapping` property still works as expected
  if (isOverrideToSetAbTestWinner(overrideConfig)) {
    return {
      ...currentDefaultConfig,
      defaultValue: overrideConfig?.defaultValue,
    };
  }

  if (overrideConfig) {
    return overrideConfig;
  }

  return currentDefaultConfig;
};

/**
 * This special function checks if from env override we receive the straightforward simple configuration
 * to permanently disable or enable feature
 * then it doesn't matter which configuration we might still have in {@link defaults.ts}.
 * This just means that feature is permanently enabled or disabled.
 *
 * @param overrideConfig override provided to function by feature name, may be undefined meaning that it's just not set.
 */
const isOverrideToSetAbTestWinner = (
  overrideConfig: FeatureToggleConfig | undefined,
) => {
  return (
    overrideConfig !== undefined &&
    overrideConfig.defaultValue !== undefined &&
    typeof overrideConfig.defaultValue === 'string' &&
    Object.keys(overrideConfig).length === 1
  );
};
